"use client";

import React, { useState, useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import CalculatorCard from "@/components/CalculatorCard";
import { CALCULATORS, DIY_CALCULATORS } from "@/lib/calculators-data";
import { articlesSummary } from "@/lib/datasets/articles";
import { getArticleImage } from "@/lib/image-mapper";

const siteName = process.env.NEXT_PUBLIC_SITE_NAME ?? "Meazora";

export default function HomePageClient() {
  // Quick Estimator Widget State
  const [quickLength, setQuickLength] = useState(12);
  const [quickWidth, setQuickWidth] = useState(10);
  const [quickType, setQuickType] = useState("flooring");

  // Search & Filter State
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");

  // Quick Estimator Calculation
  const { quickArea, quickMaterial, quickCost, quickSlug, quickRateExplanation } = useMemo(() => {
    const l = Math.max(0, quickLength);
    const w = Math.max(0, quickWidth);
    const area = l * w;
    let mat = "0 Sq Ft";
    let cost = "$0";
    let slug = "flooring";
    let rateExplanation = "";

    if (quickType === "flooring") {
      const sqftNeeded = Math.ceil(area * 1.1); // 10% waste
      const minCost = Math.round(sqftNeeded * 5.0);
      const maxCost = Math.round(sqftNeeded * 8.5);
      mat = `${sqftNeeded} Sq Ft (10% waste buffer)`;
      cost = `$${minCost.toLocaleString()} - $${maxCost.toLocaleString()}`;
      slug = "flooring";
      rateExplanation = "Based on avg. $5.00 - $8.50/sq ft (July 2026).";
    } else if (quickType === "concrete") {
      const cubicYards = (area * (4 / 12)) / 27;
      const cubicYardsWithBuffer = Number((cubicYards * 1.1).toFixed(2)); // 10% waste
      const bags = Math.ceil(cubicYardsWithBuffer * 45); // 45 bags of 80lb per yard
      const minCost = Math.round(cubicYardsWithBuffer * 140);
      const maxCost = Math.round(cubicYardsWithBuffer * 190);
      mat = `${cubicYardsWithBuffer} Cu Yd (~${bags} x 80lb bags)`;
      cost = `$${minCost.toLocaleString()} - $${maxCost.toLocaleString()}`;
      slug = "concrete";
      rateExplanation = "Based on national avg. $140 - $190/cu yd (July 2026).";
    } else if (quickType === "paint") {
      const perimeter = 2 * (l + w);
      const wallArea = Math.max(0, perimeter * 8 - 40); // subtract door/window
      const gallons = Math.max(1, Math.ceil((wallArea / 350) * 2)); // 2 coats
      const minCost = Math.round(gallons * 35);
      const maxCost = Math.round(gallons * 55);
      mat = `${gallons} Gallon${gallons > 1 ? "s" : ""} (2 coats, walls only)`;
      cost = `$${minCost.toLocaleString()} - $${maxCost.toLocaleString()}`;
      slug = "paint";
      rateExplanation = "Based on avg. $35 - $55/gallon (July 2026).";
    } else if (quickType === "deck") {
      const minCost = Math.round(area * 28);
      const maxCost = Math.round(area * 42);
      mat = `${area} Sq Ft structural decking`;
      cost = `$${minCost.toLocaleString()} - $${maxCost.toLocaleString()}`;
      slug = "deck";
      rateExplanation = "Based on avg. $28 - $42/sq ft installed (July 2026).";
    } else if (quickType === "tile") {
      const sqftNeeded = Math.ceil(area * 1.15); // 15% waste
      const minCost = Math.round(sqftNeeded * 9);
      const maxCost = Math.round(sqftNeeded * 16);
      mat = `${sqftNeeded} Sq Ft (15% waste buffer)`;
      cost = `$${minCost.toLocaleString()} - $${maxCost.toLocaleString()}`;
      slug = "tile";
      rateExplanation = "Based on avg. $9.00 - $16.00/sq ft (July 2026).";
    } else if (quickType === "siding") {
      const minCost = Math.round(area * 4.0);
      const maxCost = Math.round(area * 16.0);
      mat = `${area.toLocaleString()} Sq Ft wall area`;
      cost = `$${minCost.toLocaleString()} - $${maxCost.toLocaleString()}`;
      slug = "siding";
      rateExplanation = "Based on avg. $4.00 - $16.00/sq ft installed (July 2026).";
    }

    return { quickArea: area, quickMaterial: mat, quickCost: cost, quickSlug: slug, quickRateExplanation: rateExplanation };
  }, [quickLength, quickWidth, quickType]);

  // Project groups definitions (Empty Search view)
  const groups = useMemo(() => {
    return [
      {
        id: "diy",
        title: "Material Estimators",
        description: "Model material volumes, coverage area, and package limits for clean hardware store runs.",
        countLabel: "10 Calculators",
        items: DIY_CALCULATORS,
      },
      {
        id: "interior",
        title: "Interior Cost Estimators",
        description: "Model renovation budgets for kitchen overhauls, master bathroom upgrades, and resale values.",
        countLabel: "6 Cost Models",
        items: CALCULATORS.filter((c) => ["kitchen", "bathroom", "roi", "home-addition", "basement-finishing", "countertop"].includes(c.id)),
      },
      {
        id: "exterior",
        title: "Exterior Cost Estimators",
        description: "Estimate contractor labor and material replacement factors for roofs, decks, and windows.",
        countLabel: "7 Cost Models",
        items: CALCULATORS.filter((c) => ["roofing", "windows", "deck", "siding", "driveway", "pool", "gutters"].includes(c.id)),
      },
      {
        id: "systems",
        title: "Systems & Energy Estimators",
        description: "Model long-term returns and system upgrades for HVAC units and solar panels.",
        countLabel: "3 Cost Models",
        items: CALCULATORS.filter((c) => ["solar", "hvac", "water-heater"].includes(c.id)),
      },
    ];
  }, []);

  // Filtered groups if filter tab is clicked
  const visibleGroups = useMemo(() => {
    if (selectedFilter === "all") return groups;
    return groups.filter((g) => g.id === selectedFilter);
  }, [selectedFilter, groups]);

  // Flat matching list for active search queries
  const matchingCalculators = useMemo(() => {
    if (!searchQuery) return [];
    return CALCULATORS.filter((calc) => {
      const matchesSearch =
        calc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        calc.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        calc.keywords.some((k) => k.toLowerCase().includes(searchQuery.toLowerCase()));

      let matchesFilter = true;
      if (selectedFilter === "diy") {
        matchesFilter = calc.category === "diy";
      } else if (selectedFilter === "interior") {
        matchesFilter = ["kitchen", "bathroom", "roi"].includes(calc.id);
      } else if (selectedFilter === "exterior") {
        matchesFilter = ["roofing", "windows", "deck"].includes(calc.id);
      } else if (selectedFilter === "systems") {
        matchesFilter = ["solar", "hvac"].includes(calc.id);
      }

      return matchesSearch && matchesFilter;
    });
  }, [searchQuery, selectedFilter]);

  // Featured articles selection
  const featuredArticles = useMemo(() => {
    const slugs = ["roof-lifespan-by-material", "siding-replacement-cost-guide", "kitchen-remodel-cost-guide"];
    return articlesSummary.filter((a) => slugs.includes(a.slug));
  }, []);

  return (
    <div className="flex flex-col items-start w-full max-w-6xl mx-auto p-4 md:p-8 gap-16 py-8 transition-colors duration-150">
      
      {/* ── Hero Section ─────────────────────────────────────── */}
      <section className="w-full pt-10 pb-2 text-left grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 flex flex-col items-start gap-5">
          <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-[var(--accent)] select-none">
            SUITE OF INTERACTIVE MODELS
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-wide uppercase font-heading-condensed text-[var(--ink)] leading-[1.0] m-0">
            <span className="block font-medium">Home improvement modeling for</span>
            <span className="block font-black text-[var(--accent)]">materials & costs.</span>
          </h1>
          <p className="text-sm md:text-base text-[var(--ink-soft)] max-w-xl leading-relaxed font-sans m-0">
            Free, interactive calculators built to model material volumes, paint quantities, and project budgets. Runs entirely inside your browser sandbox with zero account requirements.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full mt-2 font-mono text-xs uppercase tracking-wider">
            <Link
              href="/calculators/"
              className="px-6 py-3 bg-[var(--accent)] text-white rounded font-bold hover:bg-[var(--accent-dark)] transition-all flex items-center justify-center gap-2 border border-[var(--accent)] shadow-sm cursor-pointer"
            >
              Explore 26 Calculators
            </Link>
            <Link
              href="/articles/"
              className="px-6 py-3 border border-[var(--border-strong)] text-[var(--ink-soft)] rounded font-bold hover:border-[var(--accent)] hover:text-[var(--accent)] transition-all flex items-center justify-center gap-2 bg-[var(--surface)] hover:bg-[var(--surface-muted)] cursor-pointer"
            >
              Read Project Guides
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Quick Estimator Widget */}
        <div className="lg:col-span-5 w-full flex flex-col gap-4">
          <div className="card-blueprint p-5 bg-[var(--surface)] border border-[var(--border)] rounded flex flex-col gap-4 text-left relative">
            <div className="flex items-center justify-between pb-2.5 border-b border-[var(--line)]">
              <span className="text-[10px] font-mono font-bold tracking-widest text-[var(--accent)] uppercase flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] animate-pulse" />
                Quick Estimator
              </span>
              <span className="text-[8px] font-mono text-[var(--accent)] uppercase tracking-wider font-semibold">
                Real-Time Model
              </span>
            </div>

            {/* Inputs */}
            <div className="grid grid-cols-2 gap-3.5">
              <div className="flex flex-col gap-1 text-left">
                <label htmlFor="quick-length" className="text-[9px] font-mono text-[var(--ink-soft)] uppercase tracking-wider font-bold">Length (ft)</label>
                <input
                  id="quick-length"
                  aria-label="Length in feet"
                  type="number"
                  value={quickLength || ""}
                  onChange={(e) => setQuickLength(Math.max(0, parseFloat(e.target.value) || 0))}
                  className="w-full px-2.5 py-1.5 text-xs border rounded bg-[var(--surface-muted)] font-mono focus:outline-none"
                  min="0"
                />
              </div>
              <div className="flex flex-col gap-1 text-left">
                <label htmlFor="quick-width" className="text-[9px] font-mono text-[var(--ink-soft)] uppercase tracking-wider font-bold">Width (ft)</label>
                <input
                  id="quick-width"
                  aria-label="Width in feet"
                  type="number"
                  value={quickWidth || ""}
                  onChange={(e) => setQuickWidth(Math.max(0, parseFloat(e.target.value) || 0))}
                  className="w-full px-2.5 py-1.5 text-xs border rounded bg-[var(--surface-muted)] font-mono focus:outline-none"
                  min="0"
                />
              </div>
            </div>

            {/* Project Type */}
            <div className="flex flex-col gap-1 text-left">
              <label htmlFor="quick-project-type" className="text-[9px] font-mono text-[var(--ink-soft)] uppercase tracking-wider font-bold">Project Type</label>
              <select
                id="quick-project-type"
                aria-label="Project Type"
                value={quickType}
                onChange={(e) => setQuickType(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs border rounded bg-[var(--surface-muted)] font-sans focus:outline-none cursor-pointer"
              >
                <option value="flooring">LVP Flooring (Planks)</option>
                <option value="concrete">Concrete Slab (4&quot; thickness)</option>
                <option value="paint">Wall Painting (2 coats)</option>
                <option value="deck">Wood Deck (Structural)</option>
                <option value="tile">Ceramic Floor Tile</option>
                <option value="siding">Siding Replacement</option>
              </select>
            </div>

            {/* Results Display */}
            <div className="bg-[var(--surface-muted)] border border-[var(--border)] rounded p-3 flex flex-col gap-2.5 font-mono text-xs">
              <div className="flex justify-between items-center text-[10px]">
                <span className="text-[var(--ink-soft)] uppercase">Total Area:</span>
                <span className="text-[var(--ink)] font-bold">{quickArea.toLocaleString()} Sq Ft</span>
              </div>
              <div className="flex justify-between items-center text-[10px] border-t border-[var(--line)] pt-2">
                <span className="text-[var(--ink-soft)] uppercase">Est. Material:</span>
                <span className="text-[var(--ink)] font-bold text-right truncate max-w-[200px]" title={quickMaterial}>
                  {quickMaterial}
                </span>
              </div>
              <div className="flex flex-col gap-1 border-t border-[var(--line)] pt-2 text-[10px]">
                <div className="flex justify-between items-center">
                  <span className="text-[var(--ink-soft)] uppercase">Est. Cost Range:</span>
                  <span className="text-[var(--rust)] font-black text-right">{quickCost}</span>
                </div>
                {quickRateExplanation && (
                  <span className="text-[9px] text-[var(--ink-faint)] leading-normal mt-0.5 text-right font-sans block">
                    {quickRateExplanation}
                  </span>
                )}
              </div>
            </div>

            {/* Launch CTA */}
            <Link
              href={`/calculators/${quickSlug}/`}
              className="w-full py-2.5 bg-[var(--accent)] text-white hover:bg-[var(--accent-dark)] transition-colors rounded text-center font-bold font-mono text-[10px] uppercase tracking-widest flex items-center justify-center gap-1.5 cursor-pointer shadow-sm"
            >
              Launch Full Calculator
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Trust / Metadata Row ────────────────────────────── */}
      <div className="w-full border-y border-[var(--line)] py-6 grid grid-cols-1 sm:grid-cols-4 gap-6 font-mono text-[10px] tracking-tight text-left">
        <div className="flex flex-col gap-0.5">
          <span className="font-bold text-[var(--ink-faint)] uppercase font-mono">DATA SOURCING</span>
          <span className="text-[var(--ink)] font-semibold flex items-center gap-1">
            <span>ACI, ASTM &amp; TCNA Codes</span>
            <Link href="/about/" className="text-[var(--accent)] hover:underline font-bold font-sans">&rarr;</Link>
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="font-bold text-[var(--ink-faint)] uppercase font-mono">PRIVACY</span>
          <span className="text-[var(--ink)] font-semibold">100% Client-Side Sandbox</span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="font-bold text-[var(--ink-faint)] uppercase font-mono">COST</span>
          <span className="text-[var(--ink)] font-semibold">Free, No Account Required</span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="font-bold text-[var(--ink-faint)] uppercase font-mono">LAST UPDATED</span>
          <span className="text-[var(--ink)] font-semibold text-xs text-[var(--accent)]">July 2026 (26 Tools, 120+ Datasets)</span>
        </div>
      </div>

      {/* ── Search & Filter Bar ───────────────────────────────── */}
      <div id="calculators-section" className="w-full flex flex-col md:flex-row gap-4 items-center justify-between border-b border-[var(--line)] pb-6 scroll-mt-20">
        <div role="tablist" className="flex flex-wrap gap-2 items-center justify-start w-full md:w-auto">
          {[
            { id: "all", label: "All Tools" },
            { id: "diy", label: "Material Estimators" },
            { id: "interior", label: "Interior Cost Estimators" },
            { id: "exterior", label: "Exterior Cost Estimators" },
            { id: "systems", label: "Systems & Energy" },
          ].map((tab) => {
            const isActive = selectedFilter === tab.id;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                aria-controls={tab.id === "all" ? "diy interior exterior systems" : tab.id}
                id={`tab-${tab.id}`}
                onClick={() => {
                  setSelectedFilter(tab.id);
                }}
                className={`h-9 px-4 font-mono text-[10px] font-bold uppercase tracking-wider rounded transition-all border cursor-pointer ${
                  isActive
                    ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                    : "bg-[var(--surface-muted)] text-[var(--ink-soft)] border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Search Input */}
        <div className="relative w-full md:w-80 shrink-0">
          <input
            type="text"
            placeholder="Type to filter tools..."
            value={searchQuery}
            aria-label="Filter calculators"
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-3 pr-8 py-2 text-xs font-mono border rounded bg-[var(--surface-muted)] text-[var(--ink)]"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              aria-label="Clear search query"
              className="absolute inset-y-0 right-2 flex items-center text-[var(--ink-faint)] hover:text-[var(--ink)] cursor-pointer border-none bg-transparent"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* ── Calculator Lists ─────────────────────────────────── */}
      <div className="w-full flex flex-col gap-12">
        {searchQuery ? (
          /* Search Results Grid */
          <div className="flex flex-col items-start gap-6">
            <div className="w-full flex items-center gap-4">
              <h2 className="text-xl font-extrabold uppercase tracking-wider text-[var(--ink)] font-heading-condensed whitespace-nowrap text-left w-full">
                Search Results ({matchingCalculators.length})
              </h2>
            </div>
            {matchingCalculators.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 w-full">
                {matchingCalculators.map((calc) => (
                  <div key={calc.id} className="relative">
                    <CalculatorCard calculator={calc} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="w-full py-12 text-center bg-[var(--surface-muted)] border border-dashed border-[var(--border)] rounded font-mono text-xs text-[var(--ink-soft)]">
                No calculators match your search parameters.
              </div>
            )}
          </div>
        ) : (
          /* Normal Grouped Lists */
          visibleGroups.map((group) => (
            <section
              key={group.id}
              id={group.id}
              role="tabpanel"
              aria-labelledby={`tab-${group.id}`}
              className="w-full flex flex-col items-start gap-6 scroll-mt-20"
            >
              <div className="w-full flex flex-col gap-2">
                <div className="w-full flex items-center gap-4">
                  <h2 className="text-2xl font-extrabold uppercase tracking-wider text-[var(--ink)] font-heading-condensed whitespace-nowrap">
                    {group.title}
                  </h2>
                  <div className="flex-1 h-px bg-[var(--line-strong)]" />
                  <span className="text-[10px] font-bold text-[var(--ink-faint)] uppercase tracking-wider font-mono">
                    {group.countLabel}
                  </span>
                </div>
                <p className="text-xs leading-relaxed text-[var(--ink-soft)] max-w-3xl -mt-1 font-sans text-left">
                  {group.description}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 w-full">
                {group.items.map((calc) => (
                  <div key={calc.id} className="relative">
                    <CalculatorCard calculator={calc} />
                  </div>
                ))}
              </div>
            </section>
          ))
        )}
      </div>

      {/* ── Featured Guides Section ───────────────────────────── */}
      <section className="w-full border-t border-[var(--line)] pt-12 pb-6 text-left">
        <div className="w-full flex items-center justify-between gap-4 mb-8">
          <div>
            <h2 className="text-2xl font-extrabold uppercase tracking-wider text-[var(--ink)] font-heading-condensed leading-none">
              Featured Guides &amp; Project Advice
            </h2>
            <p className="text-xs text-[var(--ink-soft)] mt-1.5 font-sans leading-relaxed">
              Read our trade-tested analysis on estimating renovation lifespans, choosing building materials, and budgeting.
            </p>
          </div>
          <Link
            href="/articles/"
            className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 border border-[var(--border-strong)] rounded text-xs font-mono font-bold uppercase tracking-wider text-[var(--ink-soft)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-all bg-[var(--surface)] hover:bg-[var(--surface-muted)] cursor-pointer"
          >
            All Guides
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
          {featuredArticles.map((article) => {
            const articleImage = getArticleImage(article.slug);
            return (
              <Link
                key={article.slug}
                href={`/articles/${article.slug}/`}
                className="group flex flex-col h-full bg-[var(--surface)] border border-[var(--border)] rounded p-5 card-blueprint hover:border-[var(--accent)] transition-all duration-150 relative text-left"
              >
                {/* Header Image */}
                <div className="relative w-full aspect-[16/10] rounded overflow-hidden mb-3.5 bg-[var(--surface-muted)] border border-[var(--border)]">
                  <Image
                    src={articleImage}
                    alt={article.title}
                    fill
                    sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 360px"
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mb-2 text-[8px] font-mono uppercase tracking-widest text-[var(--ink-soft)]">
                  <span className="font-bold text-[var(--accent)]">
                    {article.category}
                  </span>
                  <span>•</span>
                  <span>By {article.author.name}</span>
                  <span>•</span>
                  <span>{article.updatedAt || article.createdAt}</span>
                </div>
                <h3 className="text-sm font-extrabold text-[var(--ink)] group-hover:text-[var(--accent)] tracking-tight mb-2 line-clamp-2 font-sans transition-colors uppercase leading-snug">
                  {article.title}
                </h3>
                <p className="text-[var(--ink-soft)] text-xs leading-relaxed line-clamp-2 font-sans flex-1">
                  {article.description}
                </p>
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-[var(--line)] text-[9px] font-mono text-[var(--ink-soft)] uppercase tracking-wider">
                  <span>{article.readTime}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="group-hover:translate-x-0.5 transition-transform text-[var(--ink-soft)] group-hover:text-[var(--accent)]" aria-hidden="true">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Mobile All Guides Link */}
        <div className="w-full flex sm:hidden mt-6">
          <Link
            href="/articles/"
            className="w-full py-2.5 text-center border border-[var(--border-strong)] rounded text-xs font-mono font-bold uppercase tracking-wider text-[var(--ink-soft)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-all bg-[var(--surface)] hover:bg-[var(--surface-muted)] cursor-pointer"
          >
            All Guides &rarr;
          </Link>
        </div>
      </section>

      {/* ── Why Use Section ──────────────────────────────────── */}
      <section className="w-full border-t border-[var(--line)] pt-12 pb-6">
        <h2 className="text-2xl font-extrabold text-[var(--ink)] mb-10 font-heading-condensed uppercase tracking-wider text-left">
          Why Use {siteName}?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            {
              title: "Calibrated Sourcing & Standards",
              body: "Every calculation model is mathematically verified against official industry formulas and standards from Quikrete (concrete), Sherwin-Williams (paint coverage), LATICRETE (grout), and residential remodeling cost datasets (NAR 2025/2026, RSMeans adjustments).",
            },
            {
              title: "Zero-Server Sandbox Privacy",
              body: "Calculations execute 100% inside your local browser. No signup requirements, email logging, tracking cookies, or server data transmission. Your property dimensions and project details remain completely private.",
            },
            {
              title: "Precision Calibration",
              body: "Avoid the crude, generic square-footage estimates found on standard websites. Configure calculations with customizable safety waste factors (5% to 15%), coats of paint, roof pitch slopes, and regional labor cost multipliers.",
            },
          ].map((item) => (
            <div key={item.title} className="text-left py-1 border-t border-[var(--line-strong)] pt-4">
              <h3 className="font-bold text-[var(--ink)] text-sm mb-2 font-sans">{item.title}</h3>
              <p className="text-xs leading-relaxed text-[var(--ink-soft)] font-medium font-sans">{item.body}</p>
            </div>
          ))}
        </div>
      </section>
      
    </div>
  );
}
